import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, playlistApi, tagApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

export default function PlaylistReviewPage() {
  const { id }            = useParams();
  const { token, prefs }  = useAuth();
  const navigate          = useNavigate();

  const [playlist, setPlaylist]     = useState(null);
  const [cards, setCards]           = useState([]);    // full Flashcard objects, ordered
  const [cardTags, setCardTags]     = useState({});    // flashcard_id -> [tagName]
  const [index, setIndex]           = useState(0);
  const [showSolution, setShowSolution] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pl, tagInfos] = await Promise.all([
        playlistApi.get(id, token),
        tagApi.listAllForUser(token),
      ]);

      // Build tag map
      const ctMap = {};
      for (const info of (tagInfos || [])) {
        if (!ctMap[info.flashcard_id]) ctMap[info.flashcard_id] = [];
        ctMap[info.flashcard_id].push(info.tag);
      }
      setCardTags(ctMap);
      setPlaylist(pl);

      // Fetch each card in playlist order
      const items = (pl.items || []).slice().sort((a, b) => a.sort_order - b.sort_order);
      const fetched = await Promise.all(
        items.map(item => flashcardApi.get(item.flashcard_id, token).catch(() => null))
      );
      setCards(fetched.filter(Boolean));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  // Reset solution visibility whenever the card changes
  useEffect(() => { setShowSolution(false); }, [index]);

  const prev = () => setIndex(i => Math.max(0, i - 1));
  const next = () => setIndex(i => Math.min(cards.length - 1, i + 1));

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === ' ')          setShowSolution(s => !s);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  if (error)   return <div className="alert alert-danger">{error}</div>;

  if (cards.length === 0) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-link ps-0" onClick={() => navigate('/playlists')}>
            ← Back to Playlists
          </button>
        </div>
        <div className="text-muted">This playlist has no cards yet.</div>
      </>
    );
  }

  const card      = cards[index];
  const tags      = cardTags[card.id] || [];
  const showFen   = prefs.showFen !== false;
  const showTags  = prefs.showTagsOnView !== false;
  const cardPrefs = {
    ...prefs,
    diagramFlipped:          card.flipped          ?? prefs.diagramFlipped,
    diagramShowSideToMoveDisc: card.show_side_to_move ?? prefs.diagramShowSideToMoveDisc,
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button className="btn btn-link ps-0" onClick={() => navigate('/playlists')}>
          ← Back to Playlists
        </button>
        <span className="fw-semibold">{playlist?.title}</span>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate(`/playlists/${id}/edit`)}
        >
          Edit Playlist
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="d-flex justify-content-between small text-muted mb-1">
          <span>Card {index + 1} of {cards.length}</span>
          <span>{Math.round(((index + 1) / cards.length) * 100)}%</span>
        </div>
        <div className="progress" style={{ height: 6 }}>
          <div
            className="progress-bar"
            style={{ width: `${((index + 1) / cards.length) * 100}%`, background: 'var(--theme-brand-nav)' }}
          />
        </div>
      </div>

      {/* Card */}
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">{card.name}</h4>
              {card.to_move && (
                <span className={`badge ${card.to_move === 'W' ? 'bg-light text-dark border' : 'bg-dark'}`}>
                  {card.to_move === 'W' ? 'White to move' : 'Black to move'}
                </span>
              )}
            </div>

            <div className="card-body">
              {/* Chess diagram */}
              {card.fen && (
                <div className="mb-4">
                  <div className="d-flex flex-column flex-md-row gap-4 align-items-start">
                    <div className="flex-shrink-0">
                      <ChessDiagram fen={card.fen} prefs={cardPrefs} size={320} />
                    </div>
                    {showFen && (
                      <div className="flex-grow-1">
                        <small className="text-muted d-block mb-1 fw-semibold">FEN</small>
                        <code
                          className="user-select-all d-block p-2 bg-light rounded"
                          style={{ wordBreak: 'break-all', fontSize: '0.8rem' }}
                        >
                          {card.fen}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {showTags && tags.length > 0 && (
                <div className="mb-4 d-flex flex-wrap gap-1">
                  {tags.map(t => (
                    <span key={t} className="badge rounded-pill bg-secondary">{t}</span>
                  ))}
                </div>
              )}

              {/* Problem */}
              {card.problem && (
                <div className="mb-4">
                  <h6 className="text-muted text-uppercase small mb-2">Problem</h6>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.problem}</p>
                </div>
              )}

              {/* Solution */}
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h6 className="text-muted text-uppercase small mb-0">Solution</h6>
                  <button
                    className={`btn btn-sm ${showSolution ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setShowSolution(s => !s)}
                  >
                    {showSolution ? 'Hide Solution' : 'Show Solution'}
                  </button>
                </div>
                {showSolution ? (
                  <div className="solution-box">
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                      {card.solution || <em className="text-muted">No solution provided.</em>}
                    </p>
                  </div>
                ) : (
                  <div className="text-muted fst-italic small">Solution hidden — click to reveal</div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="d-flex justify-content-between align-items-center mt-4">
            <button
              className="btn btn-outline-secondary"
              onClick={prev}
              disabled={index === 0}
            >
              ← Previous
            </button>

            <span className="text-muted small">← → to navigate · Space to toggle solution</span>

            <button
              className="btn btn-outline-secondary"
              onClick={next}
              disabled={index === cards.length - 1}
            >
              Next →
            </button>
          </div>

          {index === cards.length - 1 && (
            <div className="text-center mt-4">
              <div className="alert alert-success">
                You've reached the end of this playlist!
              </div>
              <button className="btn btn-primary" onClick={() => { setIndex(0); setShowSolution(false); }}>
                Start Over
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
